import React, {
  useCallback, useState, useEffect,
} from 'react';
import classNames from 'classnames';
import { useSelector, useDispatch } from 'react-redux';

import { getACSStatus, requestACS } from '@alumni_v2/api/acsConnect';
import { gtmEventHandler } from '@common/utils/gtm';
import { toast } from '@common/ui/general/Toast';
import {
  View, Text, Icon, Button,
} from '@vectord/ui';
import {
  setIsSignupSidebarOpen,
} from '@alumni_v2/stores/reviews/actions';

import styles from './AcsStrip.module.scss';

const checkAndRequestACS = async () => {
  const result = await requestACS();
  const { success } = result;
  const message = success ? 'ACS Successfully Placed' : 'ACS request failed';

  if (!success) {
    toast.show({
      message: 'ACS request failed. Please try again later.',
      type: 'error',
      duration: 5000,
    });
  }

  gtmEventHandler(
    'cta',
    'acs_modal_strip_request_status',
    'click',
    'alumni_directory',
    {
      form_status: success,
      message,
    },
  );

  return success;
};

const triggerAuthFlow = () => {
  gtmEventHandler(
    'cta',
    'acs_modal_strip_login_intent',
    'click',
    'alumni_directory',
  );
  sessionStorage.setItem('requested-acs', true);
  document.getElementsByClassName('alumni-v2-auth-trigger')[0].click();
};


function RequestAcs(
  {
    onSuccessfulRequest,
  },
) {
  const isUserLoggedIn = () => window.__INITIAL_STATE__.currentUser;
  const { showExpertAcs } = window.__INITIAL_STATE__;
  const dispatch = useDispatch();

  const onACSRequest = useCallback(() => {
    if (isUserLoggedIn()) {
      const success = checkAndRequestACS();
      if (success) {
        onSuccessfulRequest();
      }
    } else {
      dispatch(setIsSignupSidebarOpen(true));
      triggerAuthFlow();
    }
  }, [dispatch, onSuccessfulRequest]);

  return (
    <>
      <View className="vd-gap-x-8 vd-items-center">
        <Icon
          name="phone-call"
          color="icon-neutral-inverse-strong"
          iconSize="lg"
          type="fill"
        />
        <View className="vd-flex-col vd-gap-y-4">
          <Text
            weight="bold"
            fontSize="b1"
            color="text-neutral-inverse-primary"
          >
            Want to connect with someone you think matches your profile?
          </Text>
          <Text color="text-neutral-inverse-primary" fontSize="b3">
            Let us help you find and connect with the right person
          </Text>
        </View>
      </View>
      <Button
        variants="hero"
        buttonType="solid"
        title={
          showExpertAcs
            ? 'Request 1-1 Expert Session'
            : 'Request 1-1 Alumni Session'
        }
        iconRight="arrow-up-right"
        size="m"
        onClick={onACSRequest}
        upperCase={false}
      />
    </>
  );
}

function AcsRequested() {
  return (
    <View className="vd-flex-row vd-gap-x-8">
      <Icon
        name="circle-wavy-check"
        color="icon-neutral-inverse-strong"
        type="fill"
      />
      <View className="vd-flex-col vd-gap-y-2">
        <Text fontSize="b2" color="text-neutral-inverse-primary" weight="bold">
          Your request for Alumni Connect Session
          has been successfully registered
        </Text>
        <Text color="text-neutral-inverse-primary" fontSize="caption">
          Our career counsellor will reach out to you within 48 hours
        </Text>
      </View>
    </View>
  );
}

export default function AcsStrip() {
  const [acsStatus, setAcsStatus] = useState(null);
  const [acsRequested, setAcsRequested] = useState(false);
  const isPaidUser = useSelector((state) => state.reviews.paidUser);
  const isUserLoggedIn = window.__INITIAL_STATE__.currentUser;
  const { showAlumniConnect } = window.__INITIAL_STATE__;


  const fetchAcsStatus = useCallback(async () => {
    if (!isUserLoggedIn || !showAlumniConnect) return;
    const status = await getACSStatus();
    setAcsStatus(status.registered);
  }, [isUserLoggedIn, showAlumniConnect]);


  useEffect(() => {
    fetchAcsStatus();
  }, [fetchAcsStatus]);

  if (!showAlumniConnect) return null;

  if (isUserLoggedIn
    && (isPaidUser || acsStatus === null || acsStatus === true)) return null;

  return (
    <View
      className={
        classNames(
          'vd-bottom-12',
          'sm:vd-bottom-24',
          'vd-w-full',
          'vd-left-10',
          'vd-bg-background-brand-boldest',
          'vd-py-12 sm:vd-px-16 vd-px-16 vd-gap-y-16',
          'vd-self-center sm:vd-flex-row vd-flex-col',
          'vd-justify-between vd-rounded-r-8',
          styles.stickyStrip,
        )
      }
    >
      {acsRequested ? <AcsRequested />
        : (
          <RequestAcs
            onSuccessfulRequest={() => setAcsRequested(true)}
          />
        )}
    </View>
  );
}
